import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { TimeSeriesPoint, RegionTrendData, KVTrendData } from '../../types/analytics';

interface TrendLineChartProps {
  data: (RegionTrendData | KVTrendData)[];
  title?: string;
  height?: number;
  showLegend?: boolean;
  showArea?: boolean;
  granularity?: 'day' | 'week' | 'month';
  compareMode?: 'regular' | 'contractual' | 'both' | 'overall';
  smooth?: boolean;
}

// Type guard for RegionTrendData
const isRegionTrendData = (entity: RegionTrendData | KVTrendData): entity is RegionTrendData => {
  return 'roName' in entity && !('kvId' in entity);
};

const TrendLineChart: React.FC<TrendLineChartProps> = ({
  data,
  title = 'Attendance Trend',
  height = 400,
  showLegend = true,
  showArea = true,
  granularity = 'day',
  compareMode = 'overall',
  smooth = true,
}) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Get all unique dates (guard against null/malformed entities up front so
    // both this pass and the series-building pass below agree on what's valid).
    const validEntities = (data || []).filter(
      (entity): entity is RegionTrendData | KVTrendData =>
        entity != null && Array.isArray(entity.timeSeries)
    );

    const allDates = new Set<string>();
    validEntities.forEach(entity => {
      entity.timeSeries.forEach(point => {
        allDates.add(point.date);
      });
    });
    const sortedDates = Array.from(allDates).sort();

    // Format dates for display
    const formatDate = (date: string) => {
      if (granularity === 'month') {
        const [year, month] = date.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
      }
      const d = new Date(date);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    };

    // Build series data. `validEntities` already excludes null/malformed
    // entries; we additionally fall back to a placeholder name when the
    // entity's display name is missing/blank — ECharts' legend crashes if a
    // series ends up with a null/undefined `name`.
    const series: any[] = [];

    validEntities.forEach((entity, idx) => {
      const rawName = isRegionTrendData(entity) ? entity.roName : entity.kvName;
      const entityName = rawName && String(rawName).trim() ? rawName : `Series ${idx + 1}`;
      const color = isRegionTrendData(entity) ? entity.color : undefined;
      
      // Create a map for quick lookup
      const dateMap = new Map<string, TimeSeriesPoint>();
      entity.timeSeries.forEach(point => {
        dateMap.set(point.date, point);
      });

      // Get values based on compare mode
      const getValueForMode = (point: TimeSeriesPoint | undefined) => {
        if (!point) return null;
        switch (compareMode) {
          case 'regular': return point.regularPercent;
          case 'contractual': return point.contractualPercent;
          case 'both': return point.overallPercent; // handled separately
          default: return point.overallPercent;
        }
      };

      if (compareMode === 'both') {
        // Add two lines per entity
        series.push({
          name: `${entityName} (Regular)`,
          type: 'line',
          data: sortedDates.map(date => getValueForMode(dateMap.get(date)) ?? dateMap.get(date)?.regularPercent ?? null),
          smooth,
          lineStyle: { width: 2, type: 'solid' },
          symbol: 'circle',
          symbolSize: 4,
          emphasis: { focus: 'series' },
          ...(showArea && {
            areaStyle: { opacity: 0.1 }
          }),
        });
        series.push({
          name: `${entityName} (Contractual)`,
          type: 'line',
          data: sortedDates.map(date => dateMap.get(date)?.contractualPercent ?? null),
          smooth,
          lineStyle: { width: 2, type: 'dashed' },
          symbol: 'diamond',
          symbolSize: 4,
          emphasis: { focus: 'series' },
        });
      } else {
        series.push({
          name: entityName,
          type: 'line',
          data: sortedDates.map(date => getValueForMode(dateMap.get(date))),
          smooth,
          lineStyle: { width: 2.5, color },
          itemStyle: { color },
          symbol: 'circle',
          symbolSize: 5,
          emphasis: { focus: 'series' },
          ...(showArea && {
            areaStyle: {
              opacity: 0.15,
              color: color ? {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: color },
                  { offset: 1, color: 'rgba(255,255,255,0)' }
                ]
              } : undefined
            }
          }),
        });
      }
    });

    return {
      title: title ? {
        text: title,
        left: 'center',
        top: 10,
        textStyle: { fontSize: 16, fontWeight: 600, color: '#374151' }
      } : undefined,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', animation: true },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#374151' },
        formatter: (params: any[]) => {
          const date = params[0]?.axisValue || '';
          let html = `<div style="font-weight: 600; margin-bottom: 8px;">${date}</div>`;
          params.forEach((param: any) => {
            if (param.value !== null && param.value !== undefined && typeof param.value === 'number') {
              html += `<div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color};"></span>
                <span>${param.seriesName}:</span>
                <span style="font-weight: 600; margin-left: auto;">${param.value.toFixed(1)}%</span>
              </div>`;
            }
          });
          return html;
        }
      },
      legend: showLegend ? {
        data: series.map(s => s.name),
        bottom: 10,
        type: 'scroll',
        textStyle: { fontSize: 11 }
      } : undefined,
      grid: {
        left: 60,
        right: 20,
        top: title ? 60 : 30,
        bottom: showLegend ? 60 : 30,
      },
      xAxis: {
        type: 'category',
        data: sortedDates.map(formatDate),
        axisLabel: {
          rotate: sortedDates.length > 15 ? 45 : 0,
          fontSize: 10,
          color: '#6b7280'
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'Attendance %',
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#6b7280'
        },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
        nameTextStyle: { fontSize: 11, color: '#6b7280', padding: [0, 0, 0, 10] }
      },
      series,
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };
  }, [data, title, showLegend, showArea, granularity, compareMode, smooth]);

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <p className="text-gray-500">No trend data available</p>
      </div>
    );
  }

  return (
    <ReactECharts 
      option={option} 
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
};

export default TrendLineChart;
